import { useState, useCallback } from "react"
import { useQuery, useMutation, useSubscription } from "urql"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { ChevronDown, Send, Radio, Zap, Download, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BulkActionBar } from "@/components/shared/bulk-action-bar"
import { SendMessageForm } from "./components/send-message-form"
import { BroadcastMessageForm } from "./components/broadcast-message-form"
import { SendDynamicMessageForm } from "./components/send-dynamic-message-form"
import { MessagesQuery, MessageReceivedSubscription, ApplicationsQuery, BulkDeleteMessagesMutation } from "@/lib/graphql/outbound"
import { useAuth } from "@/hooks/use-auth"
import { exportJSON, exportCSV } from "@/lib/export"
import type { ResultOf } from "@/lib/graphql/types"
import type { MessageStatus } from "@/lib/generated/graphql"

type MessageItem = ResultOf<typeof MessagesQuery>["messages"]["nodes"][number]

const LIMIT = 25

const columns: Column<MessageItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.eventType}</code>,
  },
  {
    header: "Application",
    cell: (row) => <span className="text-xs">{row.application.name}</span>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Idempotency Key",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground max-w-40 truncate block">
        {row.idempotencyKey}
      </code>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function MessagesPage() {
  const navigate = useNavigate()
  const { isConfigured } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState("")
  const [payloadSearchInput, setPayloadSearchInput] = useState("")
  const [sendOpen, setSendOpen] = useState(false)
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [dynamicOpen, setDynamicOpen] = useState(false)
  const debouncedSearch = useDebouncedValue(searchInput)
  const debouncedPayloadSearch = useDebouncedValue(payloadSearchInput)

  const applicationId = searchParams.get("applicationId") ?? undefined
  const status = (searchParams.get("status") as MessageStatus) || undefined
  const search = debouncedSearch || undefined
  const payloadSearch = debouncedPayloadSearch || undefined
  const pagination = useCursorPagination(LIMIT, [applicationId, status, search, payloadSearch])

  const [applications] = useQuery({ query: ApplicationsQuery })
  const [result, reexecute] = useQuery({
    query: MessagesQuery,
    variables: { applicationId, status, search, payloadSearch, after: pagination.after, first: pagination.first },
  })

  const handleNewMessage = useCallback(
    (
      _prev: ResultOf<typeof MessageReceivedSubscription> | undefined,
      response: ResultOf<typeof MessageReceivedSubscription>,
    ) => {
      if (response?.messageReceived) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription({ query: MessageReceivedSubscription, pause: !isConfigured }, handleNewMessage)

  const [, bulkDeleteMessages] = useMutation(BulkDeleteMessagesMutation)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    const res = await bulkDeleteMessages({ ids: Array.from(selectedKeys) })
    setBulkDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Deleted ${res.data?.bulkDeleteMessages ?? 0} messages`)
    setSelectedKeys(new Set())
    setBulkDeleteOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Messages" description="Outbound webhook messages">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="h-3.5 w-3.5 mr-1.5" />Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.messages.nodes ?? []).map((m) => ({
                id: m.id, eventType: m.eventType, application: m.application.name,
                status: m.status, idempotencyKey: m.idempotencyKey, createdAt: m.createdAt,
              }))
              exportJSON(rows, "messages")
            }}>Export JSON</DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.messages.nodes ?? []).map((m) => ({
                id: m.id, eventType: m.eventType, application: m.application.name,
                status: m.status, idempotencyKey: m.idempotencyKey, createdAt: m.createdAt,
              }))
              exportCSV(rows, ["id", "eventType", "application", "status", "idempotencyKey", "createdAt"], "messages")
            }}>Export CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="shadow-primary-glow transition-shadow">
              <Send className="h-4 w-4 mr-1.5" />
              Send
              <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setSendOpen(true)}>
              <Send className="h-3.5 w-3.5 mr-2" />
              Send Message
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setBroadcastOpen(true)}>
              <Radio className="h-3.5 w-3.5 mr-2" />
              Broadcast Message
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDynamicOpen(true)}>
              <Zap className="h-3.5 w-3.5 mr-2" />
              Send Dynamic Message
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={applicationId ?? "all"}
          onValueChange={(v) => setFilter("applicationId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All applications" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All applications</SelectItem>
            {applications.data?.applications.nodes.map((app) => (
              <SelectItem key={app.id} value={app.id}>{app.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={status ?? "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="DELIVERED">Delivered</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search messages..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />

        <Input
          placeholder="Search payload..."
          value={payloadSearchInput}
          onChange={(e) => setPayloadSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
        <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
      </BulkActionBar>

      <DataTable
        columns={columns}
        data={result.data?.messages.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.messages.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No messages"
        emptyDescription="Messages appear here when sent via the API"
        onRowClick={(row) => navigate(`/messages/${row.id}`)}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <SendMessageForm
        open={sendOpen}
        onOpenChange={setSendOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />
      <BroadcastMessageForm
        open={broadcastOpen}
        onOpenChange={setBroadcastOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />
      <SendDynamicMessageForm
        open={dynamicOpen}
        onOpenChange={setDynamicOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Messages"
        description={`This will permanently delete ${selectedKeys.size} selected messages and all associated data.`}
        confirmLabel="Delete"
        onConfirm={handleBulkDelete}
        loading={bulkDeleting}
        destructive
      />
    </div>
  )
}
